import * as THREE from 'three';
import { CSS3DRenderer } from 'three/examples/jsm/renderers/CSS3DRenderer.js';
import { EffectComposer } from 'three/examples/jsm/postprocessing/EffectComposer.js';
import { RenderPass } from 'three/examples/jsm/postprocessing/RenderPass.js';
import { UnrealBloomPass } from 'three/examples/jsm/postprocessing/UnrealBloomPass.js';
import { ShaderPass } from 'three/examples/jsm/postprocessing/ShaderPass.js';
import { SMAAPass } from 'three/examples/jsm/postprocessing/SMAAPass.js';
import Application from './Application';

const VignetteShader = {
    uniforms: {
        tDiffuse: { value: null as THREE.Texture | null },
        intensity: { value: 1.05 },
        darkness: { value: 0.85 },
    },
    vertexShader: `
        varying vec2 vUv;
        void main() {
            vUv = uv;
            gl_Position = projectionMatrix * modelViewMatrix * vec4(position, 1.0);
        }
    `,
    fragmentShader: `
        uniform sampler2D tDiffuse;
        uniform float intensity;
        uniform float darkness;
        varying vec2 vUv;
        void main() {
            vec4 color = texture2D(tDiffuse, vUv);
            vec2 uv = (vUv - 0.5) * 2.0;
            float r2 = dot(uv, uv);
            float vig = clamp(1.0 - r2 * intensity, 0.0, 1.0);
            color.rgb *= mix(1.0, vig, darkness);
            gl_FragColor = color;
        }
    `,
};

export default class Renderer {
    application: Application;
    instance!: THREE.WebGLRenderer;
    cssInstance!: CSS3DRenderer;
    composer!: EffectComposer;
    bloomPass!: UnrealBloomPass;
    vignettePass!: ShaderPass;
    smaaPass!: SMAAPass;

    constructor() {
        this.application = new Application();
        this.setInstance();
        this.setCssInstance();
        this.setComposer();
    }

    private setInstance() {
        this.instance = new THREE.WebGLRenderer({
            canvas: this.application.canvas,
            antialias: false, // composer 接管，由 SMAAPass 抗锯齿
            alpha: true,
            powerPreference: 'high-performance',
        });
        this.instance.setSize(this.application.sizes.width, this.application.sizes.height);
        this.instance.setPixelRatio(this.application.sizes.pixelRatio);
        this.instance.outputEncoding = THREE.sRGBEncoding;
        this.instance.toneMapping = THREE.ACESFilmicToneMapping;
        this.instance.toneMappingExposure = 0.95;
        this.instance.physicallyCorrectLights = true;
        this.instance.shadowMap.enabled = true;
        this.instance.shadowMap.type = THREE.PCFSoftShadowMap;
        this.instance.setClearColor(0x141414, 0);
    }

    private setCssInstance() {
        this.cssInstance = new CSS3DRenderer();
        this.cssInstance.setSize(
            this.application.sizes.width,
            this.application.sizes.height
        );
        const css = document.getElementById('css');
        if (css) {
            this.cssInstance.domElement.style.position = 'absolute';
            this.cssInstance.domElement.style.top = '0';
            css.appendChild(this.cssInstance.domElement);
        }
    }

    private setComposer() {
        const w = this.application.sizes.width;
        const h = this.application.sizes.height;
        const pr = this.application.sizes.pixelRatio;

        this.composer = new EffectComposer(this.instance);
        this.composer.setSize(w, h);
        this.composer.setPixelRatio(pr);

        const renderPass = new RenderPass(
            this.application.scene,
            this.application.camera.instance
        );
        this.composer.addPass(renderPass);

        // 屏幕辉光 + 高光溢出（仿 Cycles 的 emissive bloom）
        this.bloomPass = new UnrealBloomPass(
            new THREE.Vector2(w, h),
            0.55, // strength
            0.75, // radius
            0.82 // threshold — 只有偏亮的像素才进 bloom
        );
        this.composer.addPass(this.bloomPass);

        this.vignettePass = new ShaderPass(VignetteShader);
        this.composer.addPass(this.vignettePass);

        this.smaaPass = new SMAAPass(w * pr, h * pr);
        this.composer.addPass(this.smaaPass);
    }

    resize() {
        const w = this.application.sizes.width;
        const h = this.application.sizes.height;
        const pr = this.application.sizes.pixelRatio;
        this.instance.setSize(w, h);
        this.instance.setPixelRatio(pr);
        this.cssInstance.setSize(w, h);
        this.composer.setSize(w, h);
        this.composer.setPixelRatio(pr);
        this.bloomPass.setSize(w, h);
        this.smaaPass.setSize(w * pr, h * pr);
    }

    update() {
        this.composer.render();
        this.cssInstance.render(
            this.application.cssScene,
            this.application.camera.instance
        );
    }

    destroy() {
        this.instance.dispose();
        this.composer.dispose();
    }
}
